import envConfig from '@app/enviroment/enviroment';
import { DEFAULT_APP_LANGUAGE, type AppLanguage } from '@app/i18n/supportedLanguages';

// A changelog string is either already localized ({ en, el }) or a bare string.
// The bare form is tolerated so an entry added without translations still renders.
export type LocalizedText = string | Partial<Record<AppLanguage, string>>;

export interface ChangelogEntry {
  version: string;
  date: string;
  title: LocalizedText;
  items: LocalizedText[];
}

// Picks the requested language, falling back to English and then to whatever is present.
export function resolveLocalizedText(value: LocalizedText, language: AppLanguage): string {
  if (typeof value === 'string') {
    return value;
  }

  if (!value) {
    return '';
  }

  return value[language] ?? value[DEFAULT_APP_LANGUAGE] ?? Object.values(value).find(Boolean) ?? '';
}

const CHANGELOG_LAST_SEEN_KEY = 'last_seen_changelog_tag';

function normalizeVersion(version: string | null | undefined): number[] {
  if (!version) {
    return [];
  }

  return version
    .trim()
    .replace(/^v/i, '')
    .split('.')
    .map((segment) => Number.parseInt(segment, 10))
    .map((segment) => (Number.isNaN(segment) ? 0 : segment));
}

export function compareVersions(left: string | null | undefined, right: string | null | undefined): number {
  const leftSegments = normalizeVersion(left);
  const rightSegments = normalizeVersion(right);
  const maxLength = Math.max(leftSegments.length, rightSegments.length);

  for (let index = 0; index < maxLength; index += 1) {
    const leftValue = leftSegments[index] ?? 0;
    const rightValue = rightSegments[index] ?? 0;

    if (leftValue > rightValue) {
      return 1;
    }

    if (leftValue < rightValue) {
      return -1;
    }
  }

  return 0;
}

export function sortChangelogEntries(entries: ChangelogEntry[]): ChangelogEntry[] {
  return [...entries].sort((left, right) => compareVersions(right.version, left.version));
}

export function getCurrentTagVersion(): string {
  try {
    const versionTag = envConfig.getEnvKey('VERSION_TAG');

    if (typeof versionTag === 'string' && versionTag.trim().length > 0) {
      return versionTag.trim();
    }
  } catch {
    // Ignore env access errors and fall back to a stable placeholder.
  }

  return 'unknown';
}

// Controls only the "What's New" popup that opens by itself after a new release.
// The View Changelog entry in the user menu stays available either way.
//
// This flag defaults to ON, so it is off only on an explicit CHANGELOG_MODAL_ENABLED=false.
// A missing variable throws out of getEnvKey and an unsubstituted one arrives as '',
// and neither of those should silence the popup for a deployment that never set it.
export function isChangelogModalEnabled(): boolean {
  try {
    return envConfig.getEnvKey('CHANGELOG_MODAL_ENABLED') !== false;
  } catch {
    return true;
  }
}

export function getLastSeenChangelogTag(): string | null {
  try {
    return localStorage.getItem(CHANGELOG_LAST_SEEN_KEY);
  } catch {
    return null;
  }
}

export function setLastSeenChangelogTag(tag: string): void {
  try {
    localStorage.setItem(CHANGELOG_LAST_SEEN_KEY, tag);
  } catch {
    // Ignore storage errors to avoid blocking the UI.
  }
}

export function getNewChangelogEntries(
  entries: ChangelogEntry[],
  currentTag: string,
  lastSeenTag: string | null
): ChangelogEntry[] {
  const sortedEntries = sortChangelogEntries(entries).filter(
    (entry) => compareVersions(entry.version, currentTag) <= 0
  );

  if (sortedEntries.length === 0) {
    return [];
  }

  if (!lastSeenTag) {
    return [sortedEntries[0]];
  }

  return sortedEntries.filter((entry) => compareVersions(entry.version, lastSeenTag) > 0);
}
